import jsPDF from 'jspdf';

const ZERO_COORDINATE_EPSILON = 0.0001;

export function normalizeLatLon(lat, lon) {
  const la = Number(lat);
  const lo = Number(lon);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
  if (la < -90 || la > 90 || lo < -180 || lo > 180) return null;
  if (Math.abs(la) < ZERO_COORDINATE_EPSILON && Math.abs(lo) < ZERO_COORDINATE_EPSILON) return null;
  return { lat: la, lon: lo };
}

function pointToLatLon(point) {
  if (!point || !Array.isArray(point.coordinates) || point.coordinates.length < 2) return null;
  return normalizeLatLon(point.coordinates[1], point.coordinates[0]);
}

function trailPointToLatLon(entry) {
  return pointToLatLon(entry?.point);
}

function toTimestamp(value) {
  const timestamp = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : null;
}

function distanceMilesBetween(a, b) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusMiles = 3958.7613;
  const dLat = toRadians(b.lat - a.lat);
  const dLon = toRadians(b.lon - a.lon);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const haversine = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

export function trailToRouteCoords(trail, { startedAt, endedAt, maxAccuracy = 50 } = {}) {
  if (!Array.isArray(trail)) return [];
  const startMs = toTimestamp(startedAt);
  const endMs = toTimestamp(endedAt);
  const candidates = trail
    .map((entry, index) => ({
      coords: trailPointToLatLon(entry),
      atMs: toTimestamp(entry?.at),
      accuracy: Number(entry?.accuracy),
      index,
    }))
    .filter(({ coords, atMs, accuracy }) => {
      if (!coords) return false;
      if (Number.isFinite(accuracy) && (accuracy <= 0 || accuracy > maxAccuracy)) return false;
      if (startMs !== null && atMs !== null && atMs < startMs) return false;
      if (endMs !== null && atMs !== null && atMs > endMs) return false;
      return true;
    })
    .sort((left, right) => {
      if (left.atMs !== null && right.atMs !== null) return left.atMs - right.atMs;
      return left.index - right.index;
    });

  const route = [];
  let previousAccepted = null;
  for (const candidate of candidates) {
    const { coords, atMs } = candidate;
    if (previousAccepted) {
      if (previousAccepted.coords.lat === coords.lat && previousAccepted.coords.lon === coords.lon) continue;
      const elapsedHours = previousAccepted.atMs !== null && atMs !== null
        ? (atMs - previousAccepted.atMs) / 3_600_000
        : null;
      const segmentMiles = distanceMilesBetween(previousAccepted.coords, coords);
      const impliedMph = elapsedHours && elapsedHours > 0 ? segmentMiles / elapsedHours : null;
      // Ignore isolated GPS teleports while preserving legitimate highway travel.
      if (impliedMph !== null && segmentMiles > 0.2 && impliedMph > 110) continue;
    }
    route.push(coords);
    previousAccepted = candidate;
  }
  return route;
}

function firstTrailLatLon(trail) {
  if (!Array.isArray(trail)) return null;
  for (const entry of trail) {
    const coords = trailPointToLatLon(entry);
    if (coords) return coords;
  }
  return null;
}

function lastTrailLatLon(trail) {
  if (!Array.isArray(trail)) return null;
  for (let i = trail.length - 1; i >= 0; i--) {
    const coords = trailPointToLatLon(trail[i]);
    if (coords) return coords;
  }
  return null;
}

function encodePolyline(points) {
  let lastLat = 0;
  let lastLon = 0;
  let encoded = '';
  const encodeValue = (value) => {
    let coordinate = value < 0 ? ~(value << 1) : value << 1;
    let result = '';
    while (coordinate >= 0x20) {
      result += String.fromCharCode((0x20 | (coordinate & 0x1f)) + 63);
      coordinate >>= 5;
    }
    result += String.fromCharCode(coordinate + 63);
    return result;
  };
  for (const point of points) {
    const lat = Math.round(Number(point.lat) * 1e5);
    const lon = Math.round(Number(point.lon) * 1e5);
    encoded += encodeValue(lat - lastLat);
    encoded += encodeValue(lon - lastLon);
    lastLat = lat;
    lastLon = lon;
  }
  return encoded;
}

function sampleRoute(points, maxPoints) {
  if (!Array.isArray(points) || points.length <= maxPoints) return points || [];
  const sampled = [];
  for (let i = 0; i < maxPoints; i += 1) {
    sampled.push(points[Math.round((i * (points.length - 1)) / (maxPoints - 1))]);
  }
  return sampled;
}

async function fetchDrivingRoute(pickup, dropoff, mapboxToken) {
  const token = (mapboxToken || '').trim();
  if (!token || !pickup || !dropoff) return null;
  try {
    const coordinates = `${pickup.lon},${pickup.lat};${dropoff.lon},${dropoff.lat}`;
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}`
      + `?geometries=geojson&overview=full&access_token=${encodeURIComponent(token)}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const data = await response.json();
    const route = data?.routes?.[0]?.geometry?.coordinates;
    if (!Array.isArray(route) || route.length < 2) return null;
    return sampleRoute(route.map(([lon, lat]) => normalizeLatLon(lat, lon)).filter(Boolean), 180);
  } catch {
    return null;
  }
}

async function reverseGeocode(lat, lon) {
  const token = (process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '').trim();
  if (!token) return null;
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?types=address,poi&limit=1&access_token=${token}`,
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.features?.[0]?.place_name || null;
  } catch {
    return null;
  }
}

export function formatAddressOrCoords(address, lat, lon) {
  const addr = typeof address === 'string' ? address.trim() : '';
  if (addr && addr.length > 3 && !addr.toLowerCase().includes('flagdown')) return addr;
  const coords = normalizeLatLon(lat, lon);
  if (coords) return `${coords.lat.toFixed(6)}, ${coords.lon.toFixed(6)}`;
  return addr || '-';
}

function buildMapboxStaticUrl(
  { pickupLat, pickupLon, dropoffLat, dropoffLon, routeCoords = [], width = 900, height = 360 },
  mapboxToken,
) {
  const token = (mapboxToken || '').trim();
  if (!token) return '';
  // pin-l gives large, clearly visible markers on the printed page
  const mk = (id, lat, lon, color) => `pin-l-${id}+${color}(${lon},${lat})`;
  const normalizedRoute = Array.isArray(routeCoords)
    ? routeCoords.map((p) => normalizeLatLon(p?.lat, p?.lon)).filter(Boolean)
    : [];
  const overlays = [];
  if (normalizedRoute.length > 1) {
    overlays.push(`path-5+1d4ed8-0.9(${encodePolyline(normalizedRoute)})`);
  }
  if (pickupLat !== null && pickupLon !== null && dropoffLat !== null && dropoffLon !== null) {
    overlays.push(mk('a', pickupLat, pickupLon, '1e40af'), mk('b', dropoffLat, dropoffLon, 'dc2626'));
    // padding=60 prevents markers from being clipped at the auto-fit boundary
    return `https://api.mapbox.com/styles/v1/mapbox/navigation-day-v1/static/${encodeURIComponent(overlays.join(','))}/auto/${width}x${height}@2x?padding=60&access_token=${token}`;
  }
  const lat = pickupLat !== null && pickupLon !== null ? pickupLat : dropoffLat;
  const lon = pickupLat !== null && pickupLon !== null ? pickupLon : dropoffLon;
  if (lat === null || lon === null) return '';
  overlays.push(mk('pin', lat, lon, '1e40af'));
  return `https://api.mapbox.com/styles/v1/mapbox/navigation-day-v1/static/${encodeURIComponent(overlays.join(','))}/${lon},${lat},14/${width}x${height}@2x?access_token=${token}`;
}

function buildOsmStaticUrl({ pickupLat, pickupLon, dropoffLat, dropoffLon, width = 860, height = 200, zoom = 14 }) {
  const markers = [];
  if (pickupLat !== null && pickupLon !== null) markers.push(`${pickupLat},${pickupLon},red-pushpin`);
  if (dropoffLat !== null && dropoffLon !== null) markers.push(`${dropoffLat},${dropoffLon},blue-pushpin`);
  let centerLat = null;
  let centerLon = null;
  if (pickupLat !== null && pickupLon !== null && dropoffLat !== null && dropoffLon !== null) {
    centerLat = (Number(pickupLat) + Number(dropoffLat)) / 2;
    centerLon = (Number(pickupLon) + Number(dropoffLon)) / 2;
  } else if (pickupLat !== null && pickupLon !== null) {
    centerLat = pickupLat; centerLon = pickupLon;
  } else if (dropoffLat !== null && dropoffLon !== null) {
    centerLat = dropoffLat; centerLon = dropoffLon;
  }
  if (centerLat === null || centerLon === null) return '';
  const size = `${Math.min(1280, Math.max(300, Math.round(width)))}x${Math.min(1024, Math.max(120, Math.round(height)))}`;
  return `https://staticmap.openstreetmap.de/staticmap.php?center=${encodeURIComponent(`${centerLat},${centerLon}`)}&zoom=${encodeURIComponent(String(zoom))}&size=${encodeURIComponent(size)}&markers=${encodeURIComponent(markers.join('|'))}`;
}

function loadImageAsDataUrl(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
      if (img.complete && img.naturalWidth) {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      }
    } catch {
      resolve(null);
    }
  });
}

function safeAmount(v, fallback = 0) {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return n;
}

function applyMeterRounding(value, mode = 'none') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  switch (mode) {
    case 'nearest_0.1': return Math.round(amount * 10) / 10;
    case 'nearest_0.25': return Math.round(amount * 4) / 4;
    case 'nearest_0.5': return Math.round(amount * 2) / 2;
    case 'nearest_1': return Math.round(amount);
    default: return Math.round(amount * 100) / 100;
  }
}

function computeFareBreakdownLocal({ fareConfig, fareStrategy, flatRateAmount, meterMiles, waitMinutes, passengers, appliedFees }) {
  const fees = Array.isArray(appliedFees) ? appliedFees : [];
  const otherFeesTotal = fees.reduce((s, f) => s + safeAmount(f?.amount), 0);
  const passengerCount = Number(passengers || 1);
  const extraPassengers = Math.max(Math.floor(passengerCount) - 1, 0);
  const extraPassengerFare = extraPassengers * safeAmount(fareConfig?.extraPass ?? 0);
  const roundingMode = fareConfig?.meterRoundingMode || 'none';
  const surgeMultiplier =
    fareConfig?.surgeEnabled && safeAmount(fareConfig?.surgeMultiplier, 0) > 0
      ? Math.max(safeAmount(fareConfig.surgeMultiplier), 1)
      : 1;

  if (fareStrategy === 'flat' && Number.isFinite(flatRateAmount) && flatRateAmount > 0) {
    const baseFare = safeAmount(flatRateAmount, 0);
    const subtotalWithExtras = baseFare + extraPassengerFare + otherFeesTotal;
    const total = applyMeterRounding(subtotalWithExtras, roundingMode);
    return { baseFare, distanceFare: 0, waitFare: 0, extraPassengerFare, otherFeesTotal, otherFees: fees, roundingAdjustment: total - subtotalWithExtras, total };
  }

  const baseFare = safeAmount(fareConfig?.baseFare ?? 0);
  const perMile = safeAmount(fareConfig?.farePerMile ?? 0);
  const waitRate = safeAmount(fareConfig?.waitTimePerMinute ?? 0);
  const minimumFare = safeAmount(fareConfig?.minimumFare ?? 0);
  const distanceFare = Math.max(Number(meterMiles || 0), 0) * perMile;
  const waitFare = Math.max(Number(waitMinutes || 0), 0) * waitRate;

  let subtotalBeforeExtras = baseFare + distanceFare + waitFare;
  let minimumApplied = false;
  if (minimumFare > 0 && subtotalBeforeExtras < minimumFare) {
    subtotalBeforeExtras = minimumFare;
    minimumApplied = true;
  }

  const surgedSubtotal = subtotalBeforeExtras * surgeMultiplier;
  const subtotalWithExtras = surgedSubtotal + extraPassengerFare + otherFeesTotal;
  const total = applyMeterRounding(subtotalWithExtras, roundingMode);
  return { baseFare, distanceFare, waitFare, extraPassengerFare, otherFeesTotal, otherFees: fees, minimumApplied, roundingAdjustment: total - subtotalWithExtras, total, subtotalWithExtras };
}

/**
 * Map a raw booking record + fare config to the trip shape expected by buildModernReceiptPdf.
 */
export function buildTripFromBooking(booking, fareConfig) {
  const routeCoords = trailToRouteCoords(booking.driverLocationTrail, {
    startedAt: booking.tripSession?.startedAt || booking.pickedUpAt,
    endedAt: booking.completedAt || booking.droppedOffAt,
  });
  const pickupFallback =
    routeCoords[0] ||
    firstTrailLatLon(booking.driverLocationTrail) ||
    pointToLatLon(booking.driverLocation);
  const dropoffFallback =
    routeCoords[routeCoords.length - 1] ||
    lastTrailLatLon(booking.driverLocationTrail) ||
    pointToLatLon(booking.driverLocation);
  const pickupCoords = normalizeLatLon(booking.pickupLat, booking.pickupLon) || pickupFallback;
  const dropoffCoords = normalizeLatLon(booking.dropoffLat, booking.dropoffLon) || dropoffFallback;

  return {
    id: booking.bookingId || booking._id,
    customer: booking.customerName || '-',
    pickup: booking.pickupTime || null,
    dropoff: booking.droppedOffAt || booking.completedAt || null,
    origin: booking.pickupAddress || '-',
    destination: booking.dropoffAddress || '-',
    isFlagdown: booking.tripSource === 'driver' || booking.dispatchMethod === 'flagdown',
    pickupLat: pickupCoords?.lat ?? null,
    pickupLon: pickupCoords?.lon ?? null,
    dropoffLat: dropoffCoords?.lat ?? null,
    dropoffLon: dropoffCoords?.lon ?? null,
    routeCoords,
    driver: booking.driverName || booking.driver?.name || booking.driverId || 'Unassigned',
    driverId: booking.driverId || booking.driver?._id || '',
    cabNumber: booking.cabNumber || booking.assignedCab || '',
    fare: Number(booking.finalFare ?? booking.estimatedFare ?? 0),
    meterMiles: Number(booking.meterMiles ?? booking.estimatedDistanceMiles ?? 0),
    waitMinutes: Number(booking.waitMinutes ?? 0),
    passengers: Number(booking.passengers ?? 1),
    appliedFees: Array.isArray(booking.appliedFees) ? booking.appliedFees : [],
    fareStrategy: booking.fareStrategy || 'meter',
    flatRateAmount: Number(booking.flatRateAmount ?? 0),
    fareSnapshot: booking.fareSnapshot || null,
    fareConfig,
  };
}

/**
 * Build and trigger download of the modern styled PDF receipt.
 * @param {object} trip        - Use buildTripFromBooking() to produce this.
 * @param {object} company     - { name, address, phone, email }
 * @param {string} mapboxToken - Optional Mapbox access token for the map image.
 */
export async function buildModernReceiptPdf(trip, company = {}, mapboxToken = '') {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 36;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;

  const colors = {
    ink: '#111827', muted: '#64748b', faint: '#94a3b8', line: '#e2e8f0',
    panel: '#f8fafc', panelAlt: '#eff6ff', brand: '#0f172a', accent: '#2563eb',
    success: '#16a34a', white: '#ffffff',
  };

  const hexToRgb = (hex) => {
    const clean = String(hex || '#000000').replace('#', '');
    return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
  };
  const setFill = (hex) => doc.setFillColor(...hexToRgb(hex));
  const setDraw = (hex) => doc.setDrawColor(...hexToRgb(hex));
  const setText = (hex) => doc.setTextColor(...hexToRgb(hex));
  const cleanText = (value, fallback = '-') => {
    const text = value === undefined || value === null ? '' : String(value).trim();
    return text || fallback;
  };
  const money = (value) => {
    const amount = Number(value || 0);
    return amount < 0 ? `-$${Math.abs(amount).toFixed(2)}` : `$${amount.toFixed(2)}`;
  };
  const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-');
  const write = (text, x, y, options = {}) => {
    const { size = 10, color = colors.ink, style = 'normal', align, maxWidth } = options;
    doc.setFont('Helvetica', style);
    doc.setFontSize(size);
    setText(color);
    doc.text(Array.isArray(text) ? text : cleanText(text), x, y, { align, maxWidth });
  };
  const roundedPanel = (x, y, width, height, fill, stroke = colors.line, radius = 10) => {
    setFill(fill);
    setDraw(stroke);
    doc.setLineWidth(0.8);
    doc.roundedRect(x, y, width, height, radius, radius, 'FD');
  };

  const fareCfg = trip.fareConfig || null;
  const computed = computeFareBreakdownLocal({
    fareConfig: fareCfg,
    fareStrategy: trip.fareStrategy,
    flatRateAmount: trip.flatRateAmount,
    meterMiles: trip.meterMiles,
    waitMinutes: trip.waitMinutes,
    passengers: trip.passengers,
    appliedFees: trip.appliedFees,
  });
  const computedTotal = Number(computed.total ?? 0);
  const fallbackTotal = Number(trip.fare ?? 0);
  const snapshotTotal = Number(trip.fareSnapshot?.total);
  const hasFareSnapshot = Number.isFinite(snapshotTotal) && snapshotTotal >= 0;
  const fareBreakdown = hasFareSnapshot ? trip.fareSnapshot : computed;
  const receiptTotal = hasFareSnapshot
    ? snapshotTotal
    : computedTotal > 0 || fallbackTotal === 0
      ? computedTotal
      : fallbackTotal;

  const lineItems = [];
  if ((fareBreakdown.strategy || trip.fareStrategy) === 'flat' && Number(fareBreakdown.baseFare ?? 0) !== 0) {
    lineItems.push({ label: 'Flat rate', detail: 'Pre-set trip fare', amount: fareBreakdown.baseFare });
  } else {
    if (Number(fareBreakdown.baseFare ?? 0) !== 0) lineItems.push({ label: 'Base fare', amount: fareBreakdown.baseFare });
    if (Number(fareBreakdown.distanceFare ?? 0) !== 0) {
      lineItems.push({ label: 'Distance', detail: `${Number(fareBreakdown.meterMiles ?? trip.meterMiles ?? 0).toFixed(2)} mi`, amount: fareBreakdown.distanceFare });
    }
    if (Number(fareBreakdown.waitFare ?? 0) !== 0) {
      lineItems.push({ label: 'Wait time', detail: `${Number(fareBreakdown.waitMinutes ?? trip.waitMinutes ?? 0).toFixed(1)} min`, amount: fareBreakdown.waitFare });
    }
  }
  if (Number(fareBreakdown.extraPassengerFare ?? 0) !== 0) {
    lineItems.push({ label: 'Extra passenger', detail: `${Math.max(Number(fareBreakdown.passengerCount ?? trip.passengers ?? 1) - 1, 0)} additional`, amount: fareBreakdown.extraPassengerFare });
  }
  for (const fee of Array.isArray(fareBreakdown.otherFees) ? fareBreakdown.otherFees : []) {
    if (Number(fee.amount ?? 0) !== 0) lineItems.push({ label: cleanText(fee.name, 'Additional fee'), amount: fee.amount });
  }
  if (Number(fareBreakdown.roundingAdjustment ?? 0) !== 0) {
    lineItems.push({ label: 'Rounding', amount: fareBreakdown.roundingAdjustment });
  }
  if (!lineItems.length && receiptTotal !== 0) {
    lineItems.push({ label: 'Trip fare', amount: receiptTotal });
  }

  const pLat = trip.pickupLat ?? null;
  const pLon = trip.pickupLon ?? null;
  const dLat = trip.dropoffLat ?? null;
  const dLon = trip.dropoffLon ?? null;
  const routeCoords = Array.isArray(trip.routeCoords) ? trip.routeCoords : [];
  const pickupPoint = normalizeLatLon(pLat, pLon);
  const dropoffPoint = normalizeLatLon(dLat, dLon);

  // Map image — fetched at 900×360 (2.5:1) to match the PDF display ratio (mapW≈500 × mapH=200)
  let mapData = null;
  let routeLabel = 'Pickup and drop-off';
  if ((pLat !== null && pLon !== null) || (dLat !== null && dLon !== null)) {
    if ((mapboxToken || '').trim()) {
      const hasRecordedRoute = routeCoords.length > 1;
      const drivingRoute = hasRecordedRoute
        ? null
        : await fetchDrivingRoute(pickupPoint, dropoffPoint, mapboxToken);
      const displayRoute = hasRecordedRoute ? sampleRoute(routeCoords, 180) : drivingRoute || [];
      if (hasRecordedRoute) routeLabel = 'Recorded trip route';
      else if (drivingRoute) routeLabel = 'Driving route';
      try {
        mapData = await loadImageAsDataUrl(buildMapboxStaticUrl({
          pickupLat: pLat,
          pickupLon: pLon,
          dropoffLat: dLat,
          dropoffLon: dLon,
          routeCoords: displayRoute,
          width: 1000,
          height: 250,
        }, mapboxToken));
      } catch {
        mapData = null;
      }
      if (!mapData) {
        try { mapData = await loadImageAsDataUrl(buildMapboxStaticUrl({ pickupLat: pLat, pickupLon: pLon, dropoffLat: dLat, dropoffLon: dLon, width: 1000, height: 250 }, mapboxToken)); } catch { mapData = null; }
      }
    }
    // OSM as last resort when Mapbox is unavailable
    if (!mapData) {
      try { mapData = await loadImageAsDataUrl(buildOsmStaticUrl({ pickupLat: pLat, pickupLon: pLon, dropoffLat: dLat, dropoffLon: dLon, width: 1000, height: 250, zoom: 13 })); } catch { mapData = null; }
    }
  }

  // Resolve addresses — reverse-geocode GPS coordinates for flagdown/no-address trips
  const looksLikeCoords = (s) => typeof s === 'string' && /^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(s);
  let origin = formatAddressOrCoords(trip.origin, trip.pickupLat, trip.pickupLon);
  let destination = formatAddressOrCoords(trip.destination, trip.dropoffLat, trip.dropoffLon);
  const needsGeocode = (s) => !s || s.length <= 3 || looksLikeCoords(s) || s.toLowerCase().includes('flagdown');
  if (needsGeocode(origin) && pLat !== null && pLon !== null) {
    origin = (await reverseGeocode(pLat, pLon)) || (trip.isFlagdown ? 'Flagdown pickup' : 'Pickup location');
  }
  if (needsGeocode(destination) && dLat !== null && dLon !== null) {
    destination = (await reverseGeocode(dLat, dLon)) || (trip.isFlagdown ? 'Trip endpoint' : 'Drop-off location');
  }

  doc.setProperties({ title: `Trip receipt ${trip.id || ''}`, subject: 'Taxi trip receipt', author: company.name || 'TaxiOps' });

  let cursor = margin;

  // Header banner
  setFill(colors.brand);
  doc.roundedRect(margin, cursor, contentWidth, 76, 12, 12, 'F');
  write(company.name || 'TaxiOps Transportation LLC', margin + 20, cursor + 27, { size: 17, color: colors.white, style: 'bold' });
  const companyLine = [company.address, [company.phone, company.email].filter(Boolean).join(' | ')].filter(Boolean).join(' / ');
  if (companyLine) write(doc.splitTextToSize(companyLine, contentWidth * 0.56).slice(0, 2), margin + 20, cursor + 44, { size: 8, color: '#cbd5e1' });
  write('PAID RECEIPT', pageWidth - margin - 20, cursor + 22, { size: 8.5, color: '#bfdbfe', style: 'bold', align: 'right' });
  write(`Trip ${trip.id}`, pageWidth - margin - 20, cursor + 42, { size: 13, color: colors.white, style: 'bold', align: 'right' });
  write(formatDateTime(trip.pickup), pageWidth - margin - 20, cursor + 59, { size: 8, color: '#cbd5e1', align: 'right' });
  cursor += 86;

  // Total paid banner
  roundedPanel(margin, cursor, contentWidth, 58, colors.panelAlt, '#bfdbfe', 10);
  write('TOTAL PAID', margin + 18, cursor + 19, { size: 7.5, color: colors.accent, style: 'bold' });
  write(money(receiptTotal), margin + 18, cursor + 45, { size: 24, color: colors.ink, style: 'bold' });
  write(`Cab ${trip.cabNumber || '-'}`, pageWidth - margin - 18, cursor + 22, { size: 10, color: colors.ink, style: 'bold', align: 'right' });
  write(
    trip.fareStrategy === 'flat' ? 'Flat fare' : `${Number(trip.meterMiles || 0).toFixed(2)} mi  |  ${Number(trip.waitMinutes || 0).toFixed(1)} min wait`,
    pageWidth - margin - 18,
    cursor + 41,
    { size: 8.5, color: colors.muted, align: 'right' },
  );
  cursor += 68;

  // Info cards
  const summaryHeight = 60;
  const summaryColumnWidth = contentWidth / 4;
  roundedPanel(margin, cursor, contentWidth, summaryHeight, colors.panel, colors.line, 10);
  [
    { label: 'CUSTOMER', value: cleanText(trip.customer) },
    { label: 'DRIVER', value: [cleanText(trip.driver), trip.driverId ? `ID ${trip.driverId}` : ''].filter(Boolean).join(' / ') },
    { label: 'PICKUP', value: formatDateTime(trip.pickup) },
    { label: 'DROP-OFF', value: formatDateTime(trip.dropoff) },
  ].forEach((item, index) => {
    const x = margin + index * summaryColumnWidth;
    if (index > 0) {
      setDraw(colors.line);
      doc.line(x, cursor + 11, x, cursor + summaryHeight - 11);
    }
    write(item.label, x + 11, cursor + 19, { size: 7, color: colors.muted, style: 'bold' });
    write(doc.splitTextToSize(item.value, summaryColumnWidth - 22).slice(0, 2), x + 11, cursor + 36, { size: 8, color: colors.ink, style: 'bold' });
  });
  cursor += summaryHeight + 10;

  // Route card — map is 200pt tall (2.5:1 ratio matching the 900×360 fetched image)
  const mapH = 128;
  const routeCardHeight = mapData ? 204 : 84;
  roundedPanel(margin, cursor, contentWidth, routeCardHeight, colors.white, colors.line, 10);
  write('Trip route', margin + 16, cursor + 22, { size: 11, color: colors.ink, style: 'bold' });
  write(routeLabel, pageWidth - margin - 16, cursor + 22, { size: 7.5, color: colors.muted, style: 'bold', align: 'right' });
  if (mapData) {
    const mapX = margin + 12;
    const mapY = cursor + 31;
    const mapW = contentWidth - 24;
    doc.addImage(mapData, 'PNG', mapX, mapY, mapW, mapH);
    setDraw(colors.line);
    doc.roundedRect(mapX, mapY, mapW, mapH, 6, 6, 'S');
  }
  const addressY = cursor + (mapData ? 170 : 35);
  const addressColumnWidth = (contentWidth - 42) / 2;
  write('FROM', margin + 16, addressY, { size: 7, color: colors.faint, style: 'bold' });
  write(doc.splitTextToSize(origin, addressColumnWidth).slice(0, 2), margin + 16, addressY + 14, { size: 8, color: colors.ink });
  const destinationX = margin + contentWidth / 2 + 6;
  write('TO', destinationX, addressY, { size: 7, color: colors.faint, style: 'bold' });
  write(doc.splitTextToSize(destination, addressColumnWidth).slice(0, 2), destinationX, addressY + 14, { size: 8, color: colors.ink });
  cursor += routeCardHeight + 10;

  // Fare breakdown
  const printableItems = lineItems.length <= 20
    ? lineItems
    : [
        ...lineItems.slice(0, 19),
        {
          label: `Other charges (${lineItems.length - 19})`,
          amount: lineItems.slice(19).reduce((sum, item) => sum + Number(item.amount || 0), 0),
        },
      ];
  const fareColumns = printableItems.length > 6 ? 2 : 1;
  const rowsPerColumn = Math.max(1, Math.ceil(printableItems.length / fareColumns));
  const footerDividerY = pageHeight - 48;
  const maxFareHeight = footerDividerY - cursor - 10;
  const fareRowHeight = Math.max(13, Math.min(18, (maxFareHeight - 84) / rowsPerColumn));
  const fareHeight = Math.min(maxFareHeight, 84 + rowsPerColumn * fareRowHeight);
  roundedPanel(margin, cursor, contentWidth, fareHeight, colors.white, colors.line, 10);
  write('Fare breakdown', margin + 16, cursor + 23, { size: 11, color: colors.ink, style: 'bold' });
  write(`${printableItems.length} item${printableItems.length === 1 ? '' : 's'}`, pageWidth - margin - 16, cursor + 23, { size: 7.5, color: colors.muted, align: 'right' });
  const fareColumnWidth = (contentWidth - 24) / fareColumns;
  if (fareColumns === 2) {
    setDraw(colors.line);
    doc.line(margin + contentWidth / 2, cursor + 36, margin + contentWidth / 2, cursor + fareHeight - 42);
  }
  printableItems.forEach((item, index) => {
    const column = Math.floor(index / rowsPerColumn);
    const row = index % rowsPerColumn;
    const x = margin + 14 + column * fareColumnWidth;
    const y = cursor + 45 + row * fareRowHeight;
    const label = item.detail ? `${item.label} - ${item.detail}` : item.label;
    write(doc.splitTextToSize(label, fareColumnWidth - 74)[0], x, y, { size: fareRowHeight < 16 ? 7.5 : 8.5, color: colors.ink });
    write(money(item.amount), x + fareColumnWidth - 12, y, { size: fareRowHeight < 16 ? 7.5 : 8.5, color: colors.ink, style: 'bold', align: 'right' });
  });
  const totalY = cursor + fareHeight - 20;
  setDraw(colors.line);
  doc.line(margin + 14, totalY - 17, pageWidth - margin - 14, totalY - 17);
  write('Amount paid', margin + 16, totalY, { size: 11, color: colors.ink, style: 'bold' });
  write(money(receiptTotal), pageWidth - margin - 16, totalY, { size: 15, color: colors.success, style: 'bold', align: 'right' });

  // Footer
  setDraw(colors.line);
  doc.line(margin, footerDividerY, pageWidth - margin, footerDividerY);
  write('Thank you for riding with us.', margin, footerDividerY + 18, { size: 8.5, color: colors.ink, style: 'bold' });
  write(`Generated ${new Date().toLocaleString()}`, pageWidth - margin, footerDividerY + 18, { size: 7.5, color: colors.muted, align: 'right' });

  doc.save(`${trip.id || 'receipt'}.pdf`);
}
